import { type MouseEvent, useEffect, useRef, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { Checkbox } from '@mesh.ai/rabbit';
import { debounce } from 'lodash';
import { SectionWrapper } from 'screens/admin/Reviews/ReviewProcessV2Intent/components/SectionWrapper';

import { SelectDashed } from 'uiToolkitV1/Select';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { useGetSurveyReminders, useUpdateSurveyReminders } from '../dataHook';
import type { SurveyReminders } from '../types';

export const reminderOptions = [
  { label: '1 day', value: '1' },
  { label: '2 days', value: '2' },
  { label: '3 days', value: '3' },
  { label: '4 days', value: '4' },
  { label: '5 days', value: '5' },
  { label: '6 days', value: '6' },
  { label: '7 days', value: '7' },
  { label: '10 days', value: '10' },
  { label: '15 days', value: '15' },
  { label: '20 days', value: '20' },
];

const CustomOption = ({ data, isSelected, innerRef, innerProps }) => {
  // Prevent default behavior on mouse down to avoid triggering a blur that could close the menu
  const handleMouseDown = (e: MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
  };

  return (
    <Flex ref={innerRef} alignItems="center" gap="8px" p="8px" onMouseDown={handleMouseDown} {...innerProps}>
      <Checkbox id={`reminder-${data.value}`} checked={isSelected} />
      <TextBodyMedium htmlFor={`reminder-${data.value}`}>{data.label}</TextBodyMedium>
    </Flex>
  );
};

type SurveyReminderSectionProps = {
  surveyId: string;
  isReadOnly: boolean;
};

export const SurveyReminderSection = ({ surveyId, isReadOnly }: SurveyReminderSectionProps) => {
  const [reminders, setReminders] = useState<SurveyReminders>([reminderOptions[0], reminderOptions[6]]); // 1 day before and 1 week before
  const {
    data: selectedReminders,
    isLoading: isSelectedRemindersLoading,
    isError: isSelectedRemindersError,
  } = useGetSurveyReminders(surveyId);
  const { mutate: updateSurveyReminders } = useUpdateSurveyReminders();

  // format api reminders and set default reminders
  useEffect(() => {
    if (isSelectedRemindersLoading || isSelectedRemindersError || !selectedReminders) return;
    if (!Array.isArray(selectedReminders)) return;

    const formattedSelectedReminders = selectedReminders.map((reminder) => ({
      label: `${reminder.daysBeforeDeadline} ${reminder.daysBeforeDeadline === 1 ? 'day' : 'days'}`,
      value: reminder.daysBeforeDeadline.toString(),
    }));
    setReminders(formattedSelectedReminders);
  }, [isSelectedRemindersLoading, isSelectedRemindersError, selectedReminders]);

  const debouncedUpdate = useRef(
    // eslint-disable-next-line no-shadow
    debounce(({ surveyId, formattedReminders }) => {
      updateSurveyReminders({ surveyId, reminders: formattedReminders });
    }, 1000)
  ).current;

  const handleChange = (updatedSelectedOptions: SurveyReminders) => {
    const sortedReminders = updatedSelectedOptions.sort((a, b) => Number(a.value) - Number(b.value));
    setReminders(sortedReminders);
    const formattedReminders = sortedReminders.map((reminder) => ({
      daysBeforeDeadline: Number(reminder.value),
      audience: 'REVIEWER',
      reviewStage: 'SELF',
    }));
    debouncedUpdate({ surveyId, formattedReminders });
  };

  return (
    <SectionWrapper title="Reminders" subTitle="Set reminders settings" restrictAccess={isReadOnly}>
      <Flex gap="8px" alignItems="center">
        <TextBodyMedium>Send reminders</TextBodyMedium>
        <Box minW="200px">
          <SelectDashed
            placeholder="Select"
            value={reminders}
            options={reminderOptions}
            handleChange={handleChange}
            isMulti
            getOptionValue={undefined}
            getOptionLabel={undefined}
            selectRef={undefined}
            components={{ Option: CustomOption }}
            menuPlacement="top"
            styles={{ menu: (base: object) => ({ ...base, width: '200px' }) }}
          />
        </Box>
        <TextBodyMedium>before due date</TextBodyMedium>
      </Flex>
    </SectionWrapper>
  );
};
